/* eslint-disable no-param-reassign,no-console */
import i18next from 'i18next';
import * as R from 'ramda';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { getSetting, getUseLocalProductDB } from 'reducers/configs/settings';
import * as types from 'constants/productsDB';
import * as api from 'services/ErplyAPI/products';
import { addError, addWarning, dismissType } from 'actions/Error';
import { syncLocalDatabaseSO } from 'actions/connectivity';
import { openModalPage } from 'actions/modalPage';
import { modalPages as mp } from 'constants/modalPage';
import { getClientCode } from 'reducers/Login';
import { SO } from 'services/DB/types';
import { getItemsFromSO, saveItemsToSO } from 'services/DB';
import { notUndefinedOrNull } from 'utils/tsHelpers';
import { getActivePricelistIDsForPOS } from 'reducers/cachedItems/priceLists';
import { searchProductsDB } from 'services/DB/productSearch';
import { dateCompare, strToDate } from 'utils';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import {
  doClientRequest,
  requestCompleteResponse,
} from 'services/ErplyAPI/core/ErplyAPI';
import { getSelectedPosVatRate } from 'reducers/vatRatesDB';
import { getSyncIsReadyForType } from 'services/DB/getItemsFromSO';
import {
  getCachedItemsPerType,
  getCachedItemsPerTypeByIDs,
  getCachedItemsPerTypeIsReady,
} from 'reducers/cachedItems';
import { Product } from 'types/Product';
import { PriceList } from 'types/PriceList';
import updateRecordsToLocalDB from 'services/DB/updateRecordsToLocalDB';
import { getProductByID } from 'reducers/cachedItems/products';
import { getConnectionHealth } from 'reducers/connectivity/connection';
import { RootState } from 'reducers';

import { addItemsToCache } from './cachedItems';
import { setShouldUpdateGrid } from './UI';

export const getActivePriceListsByIDsAsync = (priceListIDs: string[]) => async (
  dispatch,
  getState,
) => {
  const clientCode = getClientCode(getState());
  const warehouseID = getSelectedWarehouseID(getState());
  let priceLists: PriceList[] = [];

  if (getCachedItemsPerTypeIsReady(SO.PRICE_LIST.NAME)(getState())) {
    priceLists = Object.values(
      getCachedItemsPerTypeByIDs<PriceList>(
        SO.PRICE_LIST.NAME,
        priceListIDs,
      )(getState()),
    );
  }

  if (priceListIDs.length > priceLists.length) {
    try {
      const records = await doClientRequest<PriceList>({
        request: 'getPriceLists',
        priceListIDs: priceListIDs?.join(','),
        warehouseID,
        recordsOnPage: 100,
      });
      if (records.length) {
        await saveItemsToSO(clientCode, SO.PRICE_LIST.NAME, records, {
          warehouseID,
        }).catch(e =>
          console.error('Failed to save new price lists to cache', e),
        );
        priceLists = [...priceLists, ...records];
      }
    } catch (err) {
      console.error('Failed to load active price lists', priceListIDs, err);
      const dbPriceLists = await getItemsFromSO<PriceList>(
        clientCode,
        SO.PRICE_LIST.NAME,
        priceListIDs,
      );
      priceLists = [...priceLists, ...dbPriceLists];
    }
  }

  return priceLists
    .filter(pl => {
      // default case for price lists without date
      if (pl.startDate === '0000-00-00' && pl.endDate === '0000-00-00')
        return true;
      return dateCompare(
        strToDate(pl.startDate, '0001-01-01'),
        new Date(),
        strToDate(pl.endDate, '9999-99-99'),
      );
    })
    .filter(pl => !!Number(pl.active))
    .flatMap(pl => pl.pricelistRules)
    .filter(notUndefinedOrNull);
};

/**
 * Checks the price lists of the products and returns a new list of the products
 * with additional properties attached.
 * Specifically, it adds the following properties.
 * * priceWithVat,
 * * priceListPrice: product.price,
 * * priceListPriceWithVat: priceWithVat,
 *
 * In PBIB-5366, added the ability to show vat in related products.
 *  Another approach was to move this logic to the UI component handling it.
 * However, due to some bugs the change was kept in this file.
 *
 * @todo Most probably, changes like this should be moved in the future.
 */
const applyProductPriceList = (products: Product[]) => async (
  dispatch: ThunkDispatch<unknown, unknown, Action>,
  getState,
): Promise<Product[]> => {
  // if products that we are searching for are already in redux cache, return them, since they already have pricelist applied
  const productIDs = products.map(product => String(product.productID));
  const productsFromRedux = getCachedItemsPerTypeByIDs<Product>(
    SO.PRODUCTS.NAME,
    productIDs,
  )(getState());
  const cachedProducts = Object.values<Product>(productsFromRedux);
  if (productIDs.length === cachedProducts.length) {
    return cachedProducts;
  }

  const activePriceListIDs = getActivePricelistIDsForPOS(getState());
  const priceListIDs = [...new Set(activePriceListIDs.map(id => String(id)))];

  const priceListRules = await dispatch(
    getActivePriceListsByIDsAsync(priceListIDs),
  );

  const productAndServiceRules = Object.fromEntries(
    priceListRules
      .filter(r => r.type === 'PRODUCT' || r.type === 'SERVICE')
      .map(r => [r.id, r.price]),
  );
  const groupRules = Object.fromEntries(
    priceListRules
      .filter(r => r.type === 'PRODGROUP')
      .map(r => [r.id, r.discountPercent]),
  );

  const vatRateObject = getSelectedPosVatRate(getState());
  const vatRate = vatRateObject?.rate ? Number(vatRateObject.rate) : null;
  return products.map(product => {
    const productFromReduxCache = getProductByID(product.productID)(getState());
    if (productFromReduxCache) return productFromReduxCache;
    const vat = Number(vatRate ?? product.vatrate);
    const priceListPrice = productAndServiceRules[product.productID];
    const groupDiscount = groupRules[product.productID];
    const isProductTaxFree = product.taxFree === 1;
    const priceWithVat = isProductTaxFree
      ? product.price
      : (product.price * (100 + vat)) / 100;

    if (priceListPrice) {
      return {
        ...product,
        priceWithVat,
        priceListPrice: Number(priceListPrice),
        priceListPriceWithVat: isProductTaxFree
          ? Number(priceListPrice)
          : (Number(priceListPrice) * (100 + vat)) / 100,
      };
    }

    if (groupDiscount) {
      return {
        ...product,
        priceWithVat,
        priceListPrice: (product.price * (100 - Number(groupDiscount))) / 100,
        priceListPriceWithVat:
          (priceWithVat * (100 - Number(groupDiscount))) / 100,
      };
    }
    // if no active price list, override old price list fetched from product sync
    return {
      ...product,
      priceWithVat,
      priceListPrice: product.price,
      priceListPriceWithVat: priceWithVat,
    };
  });
};

const getProductStockLevelAsync = (products: Product[]) => async (
  dispatch,
  getState,
): Promise<Product[]> => {
  if (!products.length) return [];
  const warehouseID = Number(getSelectedWarehouseID());
  const clientCode = getClientCode(null);
  const productIDs = products.map(pr => pr.productID);

  let stockLevelsDict;
  if (getCachedItemsPerTypeIsReady(SO.PRODUCT_STOCK.NAME)(getState())) {
    stockLevelsDict = getCachedItemsPerType(SO.PRODUCT_STOCK.NAME)(getState());
  } else {
    let stockLevelArray;
    try {
      stockLevelArray = await doClientRequest({
        request: 'getProductStock',
        productIDs: productIDs.join(),
        getAmountReserved: 1,
        warehouseID,
      });
    } catch (err) {
      console.error('Failed to load product stock for', productIDs, err);
      stockLevelArray = await getItemsFromSO(
        clientCode,
        SO.PRODUCT_STOCK.NAME,
        productIDs,
        { warehouseID },
      );
    }

    stockLevelsDict = Object.fromEntries(
      stockLevelArray.map(p => [p.productID, p]),
    );
  }

  return products.map(product => {
    const productFromReduxCache = getProductByID(product.productID)(getState());
    if (productFromReduxCache) return productFromReduxCache;
    const { amountInStock = 0, amountReserved = 0, reorderPoint = 0 } =
      stockLevelsDict[product.productID] ?? {};
    return {
      ...product,
      totalInStock: Number(amountInStock),
      reserved: Number(amountReserved),
      free: Number(amountInStock) - Number(amountReserved),
      reorderPoint: Number(reorderPoint),
    };
  });
};

type StockLevelRecord = {
  productID: number;
  amountInStock: string;
  amountReserved: string;
  warehouseID: number;
  reorderPoint: number;
};

/**
 * Action to update stock level after every sale
 *
 */
export const updateStockLevels = (saleRows: any[]) => async (
  dispatch,
  getState,
) => {
  const warehouseID = Number(getSelectedWarehouseID(getState()));
  const clientCode = getClientCode(getState());

  await updateRecordsToLocalDB(clientCode, SO.PRODUCT_STOCK.NAME, {}).catch(
    async err => {
      console.error(
        'Syncing stock level after sale failed. Proceeding with manual update.',
        err,
      );
      if (saleRows.length) {
        const stockLvls = await getItemsFromSO<StockLevelRecord>(
          clientCode,
          SO.PRODUCT_STOCK.NAME,
          saleRows.map(r => Number(r.productID)),
          { warehouseID },
        );
        await saveItemsToSO(clientCode, SO.PRODUCT_STOCK.NAME, [
          ...stockLvls.map((lvl, index) => ({
            ...(lvl ?? {
              productID: Number(saleRows[index].productID),
              amountReserved: '0',
              warehouseID,
            }),
            reorderPoint: Number(lvl.reorderPoint ?? 0),
            amountInStock:
              Number(lvl.amountInStock ?? 0) - Number(saleRows[index].amount),
          })),
        ]);
      }
    },
  );
};

export type ProductRequestParams = Partial<
  {
    posID: number;
    clientID: number;
    productID: number;
    productIDs: number[];
    type: string;
    groupID: number;
    name: string;
    code: string;
    code2: string;
    code3: string;
    supplierCode: string;
    code5: string;
    code6: string;
    code7: string;
    code8: string;
    searchNameIncrementally: string;
    fullTextSearchPhrase: string;
    pageNo: number;
    recordsOnPage: number;
    orderBy:
      | 'name'
      | 'code'
      | 'productID'
      | 'price'
      | 'parentProductID'
      | 'changed'
      | 'added';
    orderByDir: 'asc' | 'desc';
    getProductsFor: 'SALES' | 'ORDERING';
    warehouseID: number;
    getFields?: string;
  } & Record<
    | 'includeMatrixVariations'
    | 'active'
    | 'getPriceListPrices'
    | 'getWarehouseSpecificVAT'
    | 'quickPosProducts'
    | 'getRecipes'
    | 'getPackageInfo'
    | 'getReplacementProducts'
    | 'getRelatedProducts'
    | 'getParameters'
    | 'getPackagingMaterials'
    | 'getRelatedFiles'
    | 'getContainerInfo'
    | 'getMatrixVariations'
    | 'getAllLanguages'
    | 'getPriceCalculationSteps'
    | 'nonRefundableProduct'
    | 'searchCodeFromMiddle'
    | 'getItemsFromFirstPriceListOnly'
    | 'regularGiftCards'
    | 'getStockInfo',
    1 | 0
  >
>;

export type ProductsAndTotalReturnType = {
  products: Product[];
  total: number;
};

const convertPropsToAPIParameters = (params): ProductRequestParams =>
  R.filter(notUndefinedOrNull, {
    ...R.when(
      () => !(params.productID || params.productIDs || params.getProductsFor),
      R.assoc('active', 1),
    )(SO.PRODUCTS.EXTRA_PARAMS),
    recordsOnPage: 100,
    ...(typeof params === 'string'
      ? {
          searchNameIncrementally: params,
        }
      : params),
  });

const getProductsByIDs = (
  params: ProductRequestParams,
  options: GetProductsUniversalOptions,
): ((dispatch, getState) => Promise<ProductsAndTotalReturnType>) => async (
  dispatch: ThunkDispatch<unknown, unknown, Action>,
  getState,
): Promise<ProductsAndTotalReturnType> => {
  try {
    const { localFirst = true } = options;
    const parameters = convertPropsToAPIParameters(params);

    let ids = [
      ...new Set(
        [...(parameters.productIDs ?? []), parameters.productID].filter(
          notUndefinedOrNull,
        ),
      ),
    ]
      .map(n => Number(n))
      // Dealing with free-tex line products. These items have an ID value of 0. They must be filtered out if not they will cause the api to return all products in the database.
      .filter(id => id);

    if (!ids.length) {
      return { products: [], total: 0 };
    }

    let products: Product[] = [];

    if (localFirst) {
      const productsMatchedInMemory = getCachedItemsPerTypeByIDs<
        Product,
        number
      >(
        SO.PRODUCTS.NAME,
        ids,
      )(getState());

      const idsInMemory = Object.keys(productsMatchedInMemory);

      if (Object.values(productsMatchedInMemory).length) {
        ids = ids.filter(id => !idsInMemory.includes(id.toString()));
        products = products.concat(Object.values(productsMatchedInMemory));
      }

      if (!ids.length) {
        return { products, total: products.length };
      }

      const { products: iDBProducts } = await dispatch(
        searchProductsDB({
          ...parameters,
          productIDs: ids,
        }),
      );

      const idsInIDBProducts = iDBProducts.map(({ productID }) => productID);

      if (iDBProducts.length) {
        ids = ids.filter(id => !idsInIDBProducts.includes(id));
        products = products.concat(iDBProducts);
      }

      const productIDs = products.map(({ productID }) => productID);

      if (!ids.length) {
        return { products, total: products.length };
      }

      ids = ids.filter(id => !productIDs.includes(id));
    }

    const { records = [] } = await requestCompleteResponse<Product>({
      request: 'getProducts',
      ...parameters,
      productIDs: ids.join(),
    }).catch(err => {
      console.error('Failed to load products by ids', ids, parameters, err);
      return { records: [] };
    });

    if (records.length) {
      products = [...products, ...records];
    }

    return { products, total: products.length };
  } catch (err) {
    console.error('Failed to load products by IDs', params, options, err);
    return { products: [], total: 0 };
  }
};

const getProductByProperties = (
  params: ProductRequestParams | string,
  options: GetProductsUniversalOptions = {},
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
): Promise<ProductsAndTotalReturnType> => {
  const { localFirst = false } = options;
  const clientCode = getClientCode(getState());
  const parameters = convertPropsToAPIParameters(params);
  const productsReady = await getSyncIsReadyForType(
    clientCode,
    SO.PRODUCTS.NAME,
  );
  const assortmentsReady = await getSyncIsReadyForType(
    clientCode,
    SO.ASSORTMENTS.NAME,
  );
  if (
    getUseLocalProductDB(getState()) &&
    localFirst &&
    productsReady &&
    assortmentsReady
  ) {
    return dispatch(searchProductsDB(parameters));
  }
  try {
    const response = await requestCompleteResponse<Product>({
      request: 'getProducts',
      ...parameters,
    });
    return {
      products: response?.records ?? [],
      total: response?.status?.recordsTotal ?? 0,
    };
  } catch (err) {
    console.error('Failed to load products by params', params, options, err);
    return dispatch(searchProductsDB(parameters));
  }
};

export type ProductsUniversalReturnType = ProductsAndTotalReturnType & {
  productsDict: Record<number, Product>;
};

export type GetProductsUniversalOptions = {
  withMeta?: boolean;
  addToCachedItems?: boolean;
  localFirst?: boolean;
};

export const getProductsUniversal = (
  params: ProductRequestParams | string,
  options: GetProductsUniversalOptions = { withMeta: true },
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
): Promise<{
  products: Product[];
  productsDict: Record<string, Product>;
  total: number;
}> => {
  try {
    let products: Product[];
    let total: number;
    if (typeof params !== 'string' && (params.productID || params.productIDs)) {
      ({ products, total } = await dispatch(getProductsByIDs(params, options)));
    } else {
      ({ products, total } = await dispatch(
        getProductByProperties(params, options),
      ));
    }

    if (options.addToCachedItems || options.withMeta) {
      let cache = await dispatch(applyProductPriceList(products));
      cache = await dispatch(getProductStockLevelAsync(cache));
      if (options.addToCachedItems) {
        dispatch(
          addItemsToCache(
            SO.PRODUCTS.NAME,
            Object.fromEntries(cache.map(pr => [pr[SO.PRODUCTS.ID_PROP], pr])),
          ),
        );
      }

      if (options.withMeta) products = cache;
    }

    return {
      products,
      productsDict: Object.fromEntries(products.map(p => [p.productID, p])),
      total,
    };
  } catch (e) {
    console.error('Failed to load products', params, options, e);
    return {
      products: [],
      productsDict: {},
      total: 0,
    };
  }
};

export const setLoading = val => ({ type: types.LOADING, payload: val });

/**
 * Returns true if there was an error in the given params
 */
export const saveProduct = params => async (dispatch, getState) => {
  try {
    const productCodeUnique = getSetting('product_code_unique')(getState());
    dispatch(dismissType('addProduct')); // Dismiss previous errors

    if (params.name === '') {
      dispatch(
        addError(i18next.t('validation:product.nameRequired'), {
          dismissible: true,
          errorType: 'addProduct',
          selfDismiss: 2500,
        }),
      );
      return true;
    }

    if (params.name && params.name.length > 255) {
      dispatch(
        addError(i18next.t('validation:product.nameMaxLength', { max: 255 }), {
          dismissible: true,
          errorType: 'addProduct',
          selfDismiss: 2500,
        }),
      );
      return true;
    }

    if (params.code && params.code.length > 50) {
      dispatch(
        addError(i18next.t('validation:product.codeMaxLength', { max: 50 }), {
          dismissible: true,
          errorType: 'addProduct',
          selfDismiss: 2500,
        }),
      );
      return true;
    }

    // Ensure code uniqueness
    if (params.code && productCodeUnique) {
      const { products: prodsWithSuchCode } = await dispatch(
        getProductsUniversal(
          { code: params.code, active: 1 },
          {
            withMeta: false,
            addToCachedItems: false,
          },
        ),
      );

      const codeAlreadyExists = prodsWithSuchCode.length > 0;

      if (codeAlreadyExists) {
        dispatch(
          addError(i18next.t('validation:product.codeUnique'), {
            dismissible: true,
            errorType: 'addProduct',
            selfDismiss: 2500,
          }),
        );
        return true;
      }
    }

    await api.saveProduct(params);
    await dispatch(syncLocalDatabaseSO(SO.PRODUCTS.NAME));

    // Update UI flag so that grid knows to update displayed product data
    const productGroupID =
      params.groupID ??
      (await dispatch(
        getProductsUniversal(
          { productID: params.productID },
          { localFirst: true },
        ),
      ).then(({ products: [product] }) => product?.groupID));
    if (productGroupID) {
      await dispatch(setShouldUpdateGrid(productGroupID));
    }

    return false;
  } catch (err) {
    console.error('Failed to save product', err);
  }
};

export const openProductViewForItem = ({ productID }) => async dispatch => {
  const {
    products: [product],
  } = await dispatch(getProductsUniversal({ productID }));
  dispatch(
    openModalPage({
      component: mp.ProductView,
      props: {
        productItem: product,
      },
    }),
  );
};

export const openStockAndPriceForItem = ({ productID }) => async (
  dispatch,
  getState,
) => {
  const {
    products: [product],
  } = await dispatch(
    getProductsUniversal({ productID }, { localFirst: false }),
  );
  if (!getConnectionHealth(getState())) {
    dispatch(addWarning(i18next.t('alerts:errors.noAccessDuringOffline')));
  } else {
    dispatch(
      openModalPage({
        component: mp.stockAndPriceItem,
        props: {
          productItem: product,
        },
      }),
    );
  }
};
